import { HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';

import { catchError, map } from 'rxjs/operators';
import { Documento, Signatario } from '../models/documento.model';
import { environment } from '../../../../environments/environment';
import { BaseService } from './base.service';
import { DocumentosFiltro } from '../models/documentos-filtro.model';
import { AssinaturaPosicao, AssinaturaPosicoes, VerificacaoPosicao  } from '../models/assinatura-posicoes';
import { MotivoRecusa } from '../models/motivo-recusa.model';
import { Subject } from 'rxjs';
import { SalvaDocumentoDTO } from '../models/salva-documento.dto.model';
import { IGNORE_LOADING } from '../../../services/loading-interceptor';

@Injectable({
    providedIn: 'root'
})
export class DocumentoService extends BaseService<Documento, number> {
    private documentoAdicionadoSource = new Subject<void>();

    documentoAdicionado$ = this.documentoAdicionadoSource.asObservable();

    constructor(protected override injector: Injector) {
        super(injector, `${environment.apiUrl}`, Documento.fromJson);
    }

    buscaDocumentos(documentosFiltro: DocumentosFiltro) {
        let searchParams = new HttpParams();

        searchParams = searchParams.append('nome', documentosFiltro.nome);

        if (documentosFiltro.tipo?.id) {
            searchParams = searchParams.append('idTipoDocumento', documentosFiltro.tipo.id);
        }

        if (documentosFiltro.periodo) {
            searchParams = searchParams.append('dataInicio', documentosFiltro.periodo[0].toISOString().split('T')[0] + 'T00:00:00');
            searchParams = searchParams.append('dataFim', documentosFiltro.periodo[1].toISOString().split('T')[0] + 'T23:59:59');
        }

        if (documentosFiltro.statusAssinaturaUsuario) {
            searchParams = searchParams.append('statusAssinatura', documentosFiltro.statusAssinaturaUsuario);
        }

        if (documentosFiltro.statusDocumento) {
            searchParams = searchParams.append('status', documentosFiltro.statusDocumento);
        }

        if (documentosFiltro.idsBlocosAssinatura) {
            documentosFiltro.idsBlocosAssinatura.forEach(blocoId => {
                searchParams = searchParams.append('idsBlocosAssinatura[]', blocoId);
            });
        }

        searchParams = searchParams.append('page', documentosFiltro.pagina);

        searchParams = searchParams.append('sort', documentosFiltro.ordenarPor + ',' + documentosFiltro.ordenarEmDirecao);

        searchParams = searchParams.append('size', documentosFiltro.quantidade);

        return this.http.get<{
            content: Documento[],
            totalElements: number,
            totalPages: number
        }>(`${environment.apiUrl}/documentos`, {
            params: searchParams
        }).pipe(
            map(response => response),
            catchError(this.handleError.bind(this))
        );
    }

    downloadDocumento(id: number) {
        return this.http.get(`${environment.apiUrl}/documentos/${id}/arquivo`, {
            responseType: 'arraybuffer'
        }).pipe(
            map(response => response),
            catchError(this.handleError.bind(this)));
    }

    criaDocumento(salvaDocumentoDto: SalvaDocumentoDTO, arquivo: File) {
        const formData = new FormData();
        formData.append('documento', JSON.stringify(salvaDocumentoDto));
        formData.append('arquivo', arquivo);

        return this.http.post<Documento>(`${environment.apiUrl}/documentos`, formData)
            .pipe(
                map(response => response),
                catchError(this.handleError.bind(this)));
    }
  
    posicionaAssinaturaDocumento(id, assinaturaPosicoes: AssinaturaPosicoes) {
        return this.http.post(`${environment.apiUrl}/documentos/${id}/assinaturas`, assinaturaPosicoes)
            .pipe(
                map(response => response),
                catchError(this.handleError.bind(this)));
    }

    buscaDocumento(id: number) {
        return this.http.get<Documento>(`${environment.apiUrl}/documentos/${id}`).pipe(
            catchError(this.handleError.bind(this)));
    }

    buscaMotivosRecusarDocumento() {
        return this.http.get<MotivoRecusa[]>(`${environment.apiUrl}/motivos-recusa`).pipe(
            catchError(this.handleError.bind(this)));
    }

    recusarDocumento(documentoId: number, idMotivoRecusa: number, comentario: string) {
        return this.http.post(`${environment.apiUrl}/documentos/${documentoId}/recusa`, {
            motivoRecusaId: idMotivoRecusa,
            complemento: comentario
        }).pipe(
            catchError(this.handleError.bind(this)));
    }

    validaReLogin(reloginCallbackCode: string) {
        const verifier = localStorage.getItem('pkce_verifier');

        const body = new HttpParams()
            .set('grant_type', 'authorization_code')
            .set('client_id', `${environment.sso.clientId}`)
            .set('code', reloginCallbackCode)
            .set('redirect_uri', window.location.origin)
            .set('code_verifier', verifier);

        return this.http.post<{ id_token: string; access_token: string }>(
            `${environment.sso.serverUrl}/oauth2/token`, body.toString(), {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            }
        })
    }

    solicitaCodigoConfirmacaoAssinatura(idDocumento: number, reautenticacaoToken: string) {
        const params = new HttpParams().set('idDocumento', String(idDocumento));
        return this.http.post(`${environment.apiUrl}/confirmacao-assinatura`,
            { reautenticacaoToken }, { params }).pipe(
            catchError(this.handleError.bind(this)));
    }

    documentoAdicionado() {
        this.documentoAdicionadoSource.next();
    }

    validarAssinatura(hash: string) {
        const params = new HttpParams().set('hash', hash);
        return this.http.get(`${environment.apiUrl}/documentos/validar-assinatura`, {
            params
        }).pipe(
            map(response => response),
            catchError(this.handleError.bind(this))
        );
    }

    validarAssinaturaPublica(hash: string) {
        const params = new HttpParams().set('hash', hash);
        // Mesma API v1.0 das telas internas — ver nota em validarArquivoPublico.
        return this.http.get(`${environment.apiUrl}/documentos/validar-assinatura`, {
            params
        }).pipe(
            map(response => response),
            catchError(this.handleError.bind(this))
        );
    }

    validarArquivo(arquivo: File) {
        const form = new FormData();
        form.append('arquivo', arquivo);

        // Rota autenticada: o Bearer entra pelo interceptor OAuth (apiUrl esta em allowedUrls).
        return this.http.post(`${environment.apiUrl}/documentos/validar-arquivo`, form)
            .pipe(
                map(response => response),
                catchError(this.handleError.bind(this)));
    }

    baixarArquivoValidacaoPublica(hash: string) {
        const params = new HttpParams().set('hash', hash).set('arquivo', 'true');
        // Mesma API v1.0 das telas internas — ver nota em validarArquivoPublico.
        return this.http.get(`${environment.apiUrl}/documentos/validar-assinatura`, {
            params,
            responseType: 'arraybuffer'
        }).pipe(
            map(response => response),
            catchError(this.handleError.bind(this))
        );
    }

    validarArquivoPublico(arquivo: File) {
        const form = new FormData();
        form.append('arquivo', arquivo);

        // ASSINAGO-705: as chamadas publicas usavam o produto 'validacao' do WSO2, que nao
        // atende: a publicApiKey e recusada com 401 ("Missing Credentials") em desenv e
        // homolog, e o recurso validar-arquivo nem chegou a ser registrado la (404, "No
        // matching resource found"). Passam a usar a mesma API v1.0 das telas internas, cujos
        // endpoints de validacao sao abertos no gateway — o backend ja trata o Authorization
        // como opcional (DocumentoController.validarDocumentoPorArquivo) e, sem sessao, o
        // interceptor OAuth nao anexa Bearer nenhum.
        // Sem Content-Type explicito: o browser precisa definir o boundary do multipart.
        return this.http.post(`${environment.apiUrl}/documentos/validar-arquivo`, form).pipe(
            map(response => response),
            catchError(this.handleError.bind(this))
        );
    }

    cancelarDocumento(documentoId: number, motivoId: number, descricao: string) {
        return this.http.post(`${environment.apiUrl}/documentos/${documentoId}/cancelamento`, { motivoId, descricao })
            .pipe(
                map(response => response),
                catchError(this.handleError.bind(this)));
    }

    atualizaDocumento(salvaDocumentoDto: SalvaDocumentoDTO, id: number) {
        return this.http.put(`${environment.apiUrl}/documentos/${id}`, salvaDocumentoDto)
            .pipe(
                map(response => response),
                catchError(this.handleError.bind(this)));
    }

    buscaUsuario(termo: string) {
        return this.http.get<{
            nome: string,
            codigoUsuario: string | null,
            emails: string[],
            emailAssinago: string,
            cpf: string,
            existePortal: boolean,
            fonte: 'local' | 'api_pessoa' | 'portal'
        }[]>(`${environment.apiUrl}/usuarios/autocomplete?termo=${termo}`, {
            context: new HttpContext().set(IGNORE_LOADING, true)
        })
            .pipe(
                map(response => response),
                catchError(this.handleError.bind(this)));
    }

    aceitarConvite(hashConvite: string) {
        return this.http.post<{
            idDocumento: number,
            linkDocumento: string,
            usuario: {
                codigo: string,
                nome: string,
                email: string,
                telefone: string,
                emailIdGoias: string,
                telefoneIdGoias: string,
                empresa: string,
                cargo: string,
                aceiteTermos: boolean
            }
        }>(`${environment.apiUrl}/documentos/convites/${hashConvite}/aceitar`, null)
            .pipe(
                map(response => response),
                catchError(this.handleError.bind(this))
            );
    }

    verificarPosicaoAssinatura(documentoId: number, posicao: AssinaturaPosicao) {
        return this.http.post<VerificacaoPosicao>(
            `${environment.apiUrl}/documentos/${documentoId}/assinaturas/posicoes/check`,
            posicao
        ).pipe(
            catchError(this.handleError.bind(this))
        );
    }
}